import { useState } from 'react'
import { useTenantConfig } from '@webmono/storefront-ui/providers'
import type { MenuItem, MenuCategory } from '@webmono/storefront-ui/types'
import { formatCents, cn } from '@webmono/storefront-ui/utils'
import { useMenu, useUpdateMenuItem } from '@/lib/hooks'

function MenuItemRow({ item, onToggleAvailability }: { item: MenuItem; onToggleAvailability: () => void }) {
  const tenant = useTenantConfig()

  return (
    <div className={cn(
      'flex items-center gap-4 p-4 bg-card rounded-lg border border-border',
      !item.available && 'opacity-60'
    )}>
      {item.image && (
        <img
          src={item.image}
          alt={item.name}
          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
        />
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-foreground truncate">{item.name}</h3>
          {item.popular && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-accent/10 text-accent">Popular</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground truncate">{item.description}</p>
        <span className="font-mono text-sm text-foreground mt-1 block">
          {formatCents(item.priceCents, tenant.operations.currency)}
        </span>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <button
          onClick={onToggleAvailability}
          className={cn(
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
            item.available ? 'bg-success' : 'bg-muted'
          )}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              item.available ? 'translate-x-6' : 'translate-x-1'
            )}
          />
        </button>
        <span className={cn(
          'text-xs font-medium w-12',
          item.available ? 'text-success' : 'text-muted-foreground'
        )}>
          {item.available ? 'In Stock' : '86\'d'}
        </span>
      </div>
    </div>
  )
}

function CategorySection({
  category,
  onToggleAvailability,
}: {
  category: MenuCategory
  onToggleAvailability: (itemId: string, available: boolean) => void
}) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const unavailableCount = category.items.filter((i) => !i.available).length

  return (
    <div className="mb-6">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            className={cn('w-5 h-5 text-muted-foreground transition-transform', !isCollapsed && 'rotate-90')}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h2 className="font-heading font-semibold text-foreground">{category.name}</h2>
          <span className="text-sm text-muted-foreground">
            {category.items.length} items
          </span>
        </div>
        {unavailableCount > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-warning/10 text-warning">
            {unavailableCount} unavailable
          </span>
        )}
      </button>

      {!isCollapsed && (
        <div className="mt-3 space-y-2">
          {category.items.map((item) => (
            <MenuItemRow
              key={item.id}
              item={item}
              onToggleAvailability={() => onToggleAvailability(item.id, !item.available)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function MerchantMenuPage() {
  const { data: menu, isLoading, refetch } = useMenu()
  const updateMenuItem = useUpdateMenuItem()

  const handleToggleAvailability = async (itemId: string, available: boolean) => {
    await updateMenuItem.mutateAsync({ itemId, available })
    refetch()
  }

  const totalItems = menu?.categories.reduce((sum, c) => sum + c.items.length, 0) ?? 0
  const unavailableItems = menu?.categories.reduce(
    (sum, c) => sum + c.items.filter((i) => !i.available).length,
    0
  ) ?? 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold text-foreground">Menu Management</h1>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">
            {totalItems} items
          </span>
          {unavailableItems > 0 && (
            <span className="text-warning">
              {unavailableItems} unavailable
            </span>
          )}
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
            <svg className="w-5 h-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-foreground">Quick 86</h3>
            <p className="text-sm text-muted-foreground">
              Toggle items off when you run out. Changes reflect immediately on the customer menu.
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-12 bg-muted rounded-lg mb-3" />
              <div className="space-y-2">
                <div className="h-20 bg-muted rounded-lg" />
                <div className="h-20 bg-muted rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : menu ? (
        <div>
          {menu.categories.map((category) => (
            <CategorySection
              key={category.id}
              category={category}
              onToggleAvailability={handleToggleAvailability}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No menu items found</p>
        </div>
      )}
    </div>
  )
}
